import { useState, useEffect } from "react";
import axios from "axios";
import cacheStorage from "../../../utils/cacheStorage";
import {Search, Package, MapPin, Clock, CheckCircle, Truck, List, TrendingUp, User, MessageCircle} from "lucide-react";
import { motion } from "framer-motion";
import { useCourierLanguage } from "../contexts/CourierLanguageContext";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export function PackageList({
  navigateTo
}) {
  const {
    t
  } = useCourierLanguage();
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPackages = async () => {
      try {
        setLoading(true);
        const token = cacheStorage.getItem('access_token');
        const response = await axios.get('https://smart-cargo-backend-923182991420.us-central1.run.app/api/courier/deliveries', {
          headers: token ? {
            'Authorization': `Bearer ${token}`
          } : {}
        });
        
        const dbData = response.data;
        if (dbData) {
          const mappedPackages = dbData.map(pkg => ({
            id: `SHP-${pkg.id}`,
            customer: (typeof pkg.customer_name === 'object' ? pkg.customer_name?.String : pkg.customer_name) || "Unknown Customer",
            address: (typeof pkg.delivery_address === 'object' ? pkg.delivery_address?.String : pkg.delivery_address) || pkg.destination || "Unknown Address",
            status: pkg.status === 'Completed' ? 'completed' : 'pending',
            eta: pkg.status === 'In Transit' ? "1 hour" : "30 mins",
            priority: "normal"
          }));
          setPackages(mappedPackages);
        }
      } catch (error) {
        console.error("Error fetching packages:", error);
        setPackages([]);
      } finally {
        setLoading(false);
      }
    };

    fetchPackages();
  }, []);
  const filteredPackages = packages.filter(pkg => {
    const matchesFilter = filter === "all" || pkg.status === filter;
    const matchesSearch = searchQuery === "" || pkg.id.toLowerCase().includes(searchQuery.toLowerCase()) || pkg.customer.toLowerCase().includes(searchQuery.toLowerCase()) || pkg.address.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesFilter && matchesSearch;
  });
  const statusConfig = {
    pending: {
      label: t.pending,
      color: "#F59E0B",
      bg: "#FEF3C7",
      icon: Truck
    },
    completed: {
      label: t.completed,
      color: "#10B981",
      bg: "#D1FAE5",
      icon: CheckCircle
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    className: "min-h-screen bg---F5F6FA- pb-24",
    children: [/*#__PURE__*/_jsxs("div", {
      className: "bg-gradient-to-r from---2D58AD- to---71A4F4- text-white p-4 pb-6 sticky top-0 z-10",
      children: [/*#__PURE__*/_jsx("h1", {
        className: "text-2xl font-bold mb-4",
        children: t.myPackages
      }), /*#__PURE__*/_jsxs("div", {
        className: "relative mb-4",
        children: [/*#__PURE__*/_jsx(Search, {
          className: "absolute left-3 top-1-2 neg-translate-y-1-2 w-5 h-5 text-white-60"
        }), /*#__PURE__*/_jsx("input", {
          type: "text",
          value: searchQuery,
          onChange: e => setSearchQuery(e.target.value),
          placeholder: t.searchPlaceholder,
          className: "w-full pl-10 pr-4 py-3 bg-white-20 backdrop-blur-sm rounded-xl text-white placeholder-text-white-60 focus-outline-none focus-ring-2 focus-ring-white-50 transition-all"
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: "flex gap-2",
        children: [{
          key: "all",
          label: t.all
        }, {
          key: "pending",
          label: t.pending
        }, {
          key: "completed",
          label: t.completed
        }].map(tab => /*#__PURE__*/_jsx("button", {
          onClick: () => setFilter(tab.key),
          className: `px-4 py-2 rounded-xl font-medium text-sm transition-colors ${filter === tab.key ? "bg-white text-neg-2D58AD-" : "bg-white-20 text-white hover-bg-white-30"}`,
          children: tab.label
        }, tab.key))
      })]
    }), /*#__PURE__*/_jsx("div", {
      className: "p-4 space-y-3",
      children: loading ? /*#__PURE__*/_jsx("div", {
        className: "text-center py-12",
        children: /*#__PURE__*/_jsx("p", {
          className: "text-neg-708094-",
          children: t.loading || "Loading..."
        })
      }) : filteredPackages.length === 0 ? /*#__PURE__*/_jsxs("div", {
        className: "text-center py-12",
        children: [/*#__PURE__*/_jsx(Package, {
          className: "w-16 h-16 text-neg-708094- mx-auto mb-4 opacity-50"
        }), /*#__PURE__*/_jsx("p", {
          className: "text-neg-708094-",
          children: t.noPackagesFound
        })]
      }) : filteredPackages.map((pkg, index) => {
        const config = statusConfig[pkg.status];
        const StatusIcon = config.icon;
        return /*#__PURE__*/_jsxs(motion.div, {
          initial: {
            opacity: 0,
            y: 20
          },
          animate: {
            opacity: 1,
            y: 0
          },
          transition: {
            delay: index * 0.05
          },
          onClick: () => navigateTo("package-detail", pkg.id),
          className: "bg-white rounded-2xl p-4 shadow-sm hover-shadow-md transition-all cursor-pointer",
          children: [/*#__PURE__*/_jsxs("div", {
            className: "flex items-start justify-between mb-3",
            children: [/*#__PURE__*/_jsxs("div", {
              children: [/*#__PURE__*/_jsx("p", {
                className: "font-bold text---000080- mb-1",
                children: pkg.id
              }), /*#__PURE__*/_jsx("p", {
                className: "text-sm text-neg-708094-",
                children: pkg.customer
              })]
            }), /*#__PURE__*/_jsxs("div", {
              className: "px-3 py-1 rounded-full flex items-center gap-1",
              style: {
                backgroundColor: config.bg,
                color: config.color
              },
              children: [/*#__PURE__*/_jsx(StatusIcon, {
                className: "w-3-5 h-3-5"
              }), /*#__PURE__*/_jsx("span", {
                className: "text-xs font-semibold",
                children: config.label
              })]
            })]
          }), /*#__PURE__*/_jsxs("div", {
            className: "flex items-center gap-2 text-sm text-neg-708094- mb-2",
            children: [/*#__PURE__*/_jsx(MapPin, {
              className: "w-4 h-4"
            }), /*#__PURE__*/_jsx("span", {
              className: "flex-1",
              children: pkg.address
            })]
          }), /*#__PURE__*/_jsxs("div", {
            className: "flex items-center justify-between",
            children: [/*#__PURE__*/_jsxs("div", {
              className: "flex items-center gap-2 text-sm text-neg-708094-",
              children: [/*#__PURE__*/_jsx(Clock, {
                className: "w-4 h-4"
              }), /*#__PURE__*/_jsx("span", {
                children: pkg.eta
              })]
            }), pkg.priority === "high" && /*#__PURE__*/_jsx("span", {
              className: "px-2 py-1 bg-red-50 text-red-600 text-xs font-semibold rounded-full",
              children: t.highPriority
            })]
          })]
        }, pkg.id);
      })
    }), /*#__PURE__*/_jsx("div", {
      className: "fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 safe-area-bottom",
      children: /*#__PURE__*/_jsxs("div", {
        className: "flex items-center justify-around max-w-md mx-auto",
        children: [/*#__PURE__*/_jsxs("button", {
          onClick: () => navigateTo("dashboard"),
          className: "flex flex-col items-center gap-1 text-neg-708094-",
          children: [/*#__PURE__*/_jsx(Package, {
            className: "w-6 h-6"
          }), /*#__PURE__*/_jsx("span", {
            className: "text-xs font-medium",
            children: t.home
          })]
        }), /*#__PURE__*/_jsxs("button", {
          onClick: () => navigateTo("packages"),
          className: "flex flex-col items-center gap-1 text-neg-2D58AD-",
          children: [/*#__PURE__*/_jsx(List, {
            className: "w-6 h-6"
          }), /*#__PURE__*/_jsx("span", {
            className: "text-xs font-medium",
            children: t.packages
          })]
        }), /*#__PURE__*/_jsxs("button", {
          onClick: () => navigateTo("messages"),
          className: "flex flex-col items-center gap-1 text-neg-708094-",
          children: [/*#__PURE__*/_jsx(MessageCircle, {
            className: "w-6 h-6"
          }), /*#__PURE__*/_jsx("span", {
            className: "text-xs font-medium",
            children: "Messages"
          })]
        }), /*#__PURE__*/_jsxs("button", {
          onClick: () => navigateTo("performance"),
          className: "flex flex-col items-center gap-1 text-neg-708094-",
          children: [/*#__PURE__*/_jsx(TrendingUp, {
            className: "w-6 h-6"
          }), /*#__PURE__*/_jsx("span", {
            className: "text-xs font-medium",
            children: t.performance
          })]
        }), /*#__PURE__*/_jsxs("button", {
          onClick: () => navigateTo("profile"),
          className: "flex flex-col items-center gap-1 text-neg-708094-",
          children: [/*#__PURE__*/_jsx(User, {
            className: "w-6 h-6"
          }), /*#__PURE__*/_jsx("span", {
            className: "text-xs font-medium",
            children: t.profile
          })]
        })]
      })
    })]
  });
}